import { el } from './svg';

/** Milliseconds per board unit of wire — how fast the power travels. */
export const RATE = 1.5;

/**
 * Schedules the travelling surge: wires going live in wire-length order, with
 * a spark riding each new span.
 *
 * Every timer it starts is cancellable, because the player can tap again
 * mid-surge and the whole animation has to be abandoned cleanly.
 */
export class SurgeAnimator {
  private timers: ReturnType<typeof setTimeout>[] = [];

  constructor(
    private readonly layer: () => SVGGElement,
    /** Read per call, not cached — the settings switch can flip mid-session. */
    private readonly reduceMotion: () => boolean,
  ) {}

  /** Abandon every pending step. */
  clear(): void {
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
  }

  /** Run `fn` after `ms`, or immediately when motion is reduced. */
  later(fn: () => void, ms: number): void {
    if (this.reduceMotion() || ms <= 0) {
      fn();
      return;
    }
    this.timers.push(setTimeout(fn, ms));
  }

  /** Send a spark along `d`, arriving in proportion to the span's length. */
  launchSpark(d: string, len: number, reversed: boolean, delay: number): void {
    if (this.reduceMotion()) return;
    this.timers.push(
      setTimeout(() => {
        const sp = el('circle', { r: 4.5, class: 'spark' }, this.layer());
        const am = el(
          'animateMotion',
          { dur: ((len * RATE) / 1000).toFixed(3) + 's', path: d, fill: 'freeze' },
          sp,
        );
        if (reversed) {
          am.setAttribute('keyPoints', '1;0');
          am.setAttribute('keyTimes', '0;1');
          am.setAttribute('calcMode', 'linear');
        }
        if (am.beginElement) {
          try {
            am.beginElement();
          } catch {
            /* browsers without SMIL just skip the spark */
          }
        }
        this.timers.push(setTimeout(() => sp.remove(), len * RATE + 120));
      }, delay),
    );
  }
}
